import {
	Box,
	Divider,
	Flex,
	Grid,
	Group,
	Image,
	Menu,
	Skeleton,
	Stack,
	Text,
	Tooltip,
} from '@mantine/core';
import {
	IconBuildingStore,
	IconCheck,
	IconEdit,
	IconTrash,
} from '@tabler/icons-react';
import { type ReactNode, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DeleteModal } from '../../../components/DeleteModal';
import { TableRowMenu } from '../../../components/TableRowMenu';
import { useModal } from '../../../hooks/useModal';
import { defaultIconStyle } from '../../../theme/styling/iconStyling';
import { mutate } from '../../../util/api/mutate';
import { query, queryImage } from '../../../util/api/query';
import { formatToDateString } from '../../../util/formatting';
import { undefinedCheck } from '../../../util/undefinedCheck';
import type { BranchDto } from '../../branches/types/BranchTypes';
import { useIsModeratable } from '../hooks/useIsModeratable';
import type { RearDisplayDto } from '../types/RearDisplayDto';
import { BranchesModal } from './BranchesModal';
import { EditRearDisplayModal } from './EditRearDisplayModal';
import { ModerateModal } from './ModerateModal';

const imageProps = {
	h: '13vh',
	radius: 'sm',
};

interface RearDisplayRowProps {
	initialAdvert: RearDisplayDto;
	refreshTable: () => void;
}

export function RearDisplayRow({
	initialAdvert,
	refreshTable,
}: RearDisplayRowProps) {
	const { organisation } = useParams();
	const [rearDisplay, setRearDisplay] = useState(initialAdvert);

	const deleteModal = useModal();
	const moderateModal = useModal();
	const branchesModal = useModal();
	const editModal = useModal();

	const imageQuery = queryImage(
		`${organisation}/rear-display/image/${rearDisplay.id}`,
	);

	const branchesQuery = query<BranchDto[]>(
		`${organisation}/rear-display/branches/${rearDisplay.id}`,
		branchesModal.isOpen,
	);

	const deleteMutation = mutate<boolean>(
		`${organisation}/rear-display/delete/${rearDisplay.id}`,
	);

	const disabledMessage = useIsModeratable(rearDisplay);

	return (
		<>
			<DeleteModal
				close={deleteModal.close}
				isOpened={deleteModal.isOpen}
				titleType={'Rear Display'}
				mutation={deleteMutation}
				onComplete={refreshTable}
				itemDescription={rearDisplay.description}
			/>
			<ModerateModal
				close={moderateModal.close}
				isOpened={moderateModal.isOpen}
				displayDescription={rearDisplay.description}
				advertId={rearDisplay.id}
				triggerUpdateInList={(r) => {
					setRearDisplay(r);
				}}
			/>
			<BranchesModal
				close={branchesModal.close}
				isOpened={branchesModal.isOpen}
				query={branchesQuery}
			/>
			<EditRearDisplayModal
				close={editModal.close}
				isOpened={editModal.isOpen}
				triggerUpdateInList={(r) => {
					setRearDisplay(r);
					imageQuery.remove();
					branchesQuery.remove();
				}}
				defaultRearDisplay={{
					id: rearDisplay.id,
					branchIds: rearDisplay.branchIds || [],
					description: rearDisplay.description,
					startDate: new Date(rearDisplay.startDate),
					endDate: new Date(rearDisplay.endDate),
					isEdit: true,
				}}
			/>
			<Box py={20} px={20} mb={10}>
				<Grid w={'100%'}>
					<Grid.Col span={2.5}>
						{imageQuery.isLoading || !imageQuery.data ? (
							<Skeleton {...imageProps} />
						) : (
							<Image
								{...imageProps}
								src={URL.createObjectURL(imageQuery.data)}
							/>
						)}
					</Grid.Col>
					<Grid.Col span={9}>
						<Group h={'100%'} w={'100%'} align={'center'} ml={20}>
							<Group h={'90%'} w={'100%'} align={'center'}>
								<Stack justify="space-between" h={'100%'} w={'22%'}>
									<Information
										title={'Description'}
										child={undefinedCheck(rearDisplay.description)}
									/>
									<Information
										title={'Moderated By'}
										child={undefinedCheck(
											rearDisplay.moderatedBy ?? 'Unmoderated',
										)}
									/>
								</Stack>
								<Stack justify="space-between" h={'100%'} w={'22%'}>
									<Tooltip
										label={
											undefinedCheck(
												rearDisplay.name + rearDisplay.fileExtension,
											) ?? 'Meow'
										}
									>
										<Information
											title={'File Name'}
											child={undefinedCheck(
												rearDisplay.name + rearDisplay.fileExtension,
											)}
										/>
									</Tooltip>
									<Information
										title={'File Type'}
										child={undefinedCheck(rearDisplay.fileExtension)}
									/>
								</Stack>
								<Stack justify="space-between" h={'100%'} w={'22%'}>
									<Information
										title={'Start Date'}
										child={formatToDateString(rearDisplay.startDate)}
									/>
									<Information
										title={'End Date'}
										child={formatToDateString(rearDisplay.endDate)}
									/>
								</Stack>
								<Stack justify="space-between" h={'100%'} w={'22%'}>
									<Information
										title={'Created Date'}
										child={formatToDateString(rearDisplay.createdDate)}
									/>
									<Information
										title={'Created By'}
										child={undefinedCheck(rearDisplay.createdBy)}
									/>
								</Stack>
							</Group>
						</Group>
					</Grid.Col>
					<Grid.Col span={0.5}>
						<Flex align={'center'} justify={'center'} h={'100%'}>
							<TableRowMenu>
								<Menu.Item
									leftSection={<IconBuildingStore {...defaultIconStyle} />}
									onClick={() => branchesModal.open()}
								>
									View Branches
								</Menu.Item>
								<Tooltip
									label={disabledMessage}
									hidden={disabledMessage === undefined}
								>
									<Menu.Item
										leftSection={<IconCheck {...defaultIconStyle} />}
										onClick={() => moderateModal.open()}
										disabled={disabledMessage !== undefined}
									>
										Moderate
									</Menu.Item>
								</Tooltip>
								<Menu.Item
									leftSection={<IconEdit {...defaultIconStyle} />}
									onClick={() => editModal.open()}
								>
									Edit
								</Menu.Item>
								<Menu.Item
									leftSection={<IconTrash {...defaultIconStyle} />}
									c={'red'}
									onClick={() => deleteModal.open()}
								>
									Delete
								</Menu.Item>
							</TableRowMenu>
						</Flex>
					</Grid.Col>
				</Grid>
			</Box>
			<Divider />
		</>
	);
}

export function Information({
	title,
	child,
}: { title: string; child: ReactNode }) {
	return (
		<Box w={'80%'}>
			<Text size={'sm'} fw={500}>
				{title}
			</Text>
			<Text size={'sm'} lineClamp={1} w={'80%%'}>
				{child}
			</Text>
		</Box>
	);
}
